import { ContentBodyTypeEnum } from "@/types"
import type { LookupResult } from "@/types/contents"

// Client for POST /content/lookup. Returns the parsed contract on 200, or
// null for any non-200 (429 rate-limit, 5xx, network error) — callers treat
// null as "couldn't look up, fall back to manual entry", matching the
// unreachable UX. CSRF token is read from the Rails csrf_meta_tags meta tag.

function csrfToken(): string {
  return (
    document
      .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

export async function lookupUrl(
  url: string,
  signal?: AbortSignal,
): Promise<LookupResult | null> {
  try {
    const response = await fetch("/content/lookup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({ url }),
      signal,
    })

    if (!response.ok) return null
    return (await response.json()) as LookupResult
  } catch {
    // AbortError (stale/cancelled) or network failure — both manual-fallback.
    return null
  }
}

/** A result we can turn into a track (something was actually resolved). */
export function isUsable(result: LookupResult | null): result is LookupResult {
  return (
    result != null &&
    (result.status === "ok" || result.status === "partial") &&
    !!result.content_body_type &&
    !!result.fields.title
  )
}

// ---------------------------------------------------------------------------
// Mapping a resolved lookup → the exact nested payload the unchanged
// `POST /playlists/:id/tracks` create accepts (mirrors tracks/new.tsx). Every
// value stays editable later through the normal track form.
// ---------------------------------------------------------------------------

// A `type` (not `interface`) so it stays structurally assignable to Inertia's
// FormDataConvertible request payload — named interfaces lack the implicit
// index signature that assignability needs.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type NewTrackPayload = {
  notes: string
  tag_ids: number[]
  post_attributes: {
    title: string
    creator: string
    canonical_url: string
    thumbnail_url: string
    content_body_type: ContentBodyTypeEnum
    content_body_attributes: Record<string, string | number | null>
    published: boolean
  }
}

const trimmedOr = (value: string | null | undefined, fallback: string) => {
  const trimmed = value?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : fallback
}

export function lookupToTrackPayload(
  result: LookupResult,
  url: string,
  notes = "",
): NewTrackPayload {
  const type = result.content_body_type ?? ContentBodyTypeEnum.article
  const f = result.fields

  return {
    notes,
    tag_ids: [],
    post_attributes: {
      title: trimmedOr(f.title, url),
      creator: trimmedOr(f.creator, ""),
      canonical_url: url,
      thumbnail_url: f.thumbnail_url ?? "",
      content_body_type: type,
      content_body_attributes: contentBodyAttributes(type, result),
      published: false,
    },
  }
}

// Each subtype carries its own required fields (duration must be > 0 on
// video/audio; article needs a body). We satisfy them from the lookup,
// falling back to safe placeholders the recipient can refine.
function contentBodyAttributes(
  type: ContentBodyTypeEnum,
  result: LookupResult,
): Record<string, string | number | null> {
  const f = result.fields
  switch (type) {
    case ContentBodyTypeEnum.video:
      return {
        source: result.source ?? "Other",
        duration_seconds: f.duration_seconds ?? 1,
        aspect: f.aspect ?? "16:9",
      }
    case ContentBodyTypeEnum.audio:
      return {
        source: result.source ?? "Other",
        duration_seconds: f.duration_seconds ?? 1,
      }
    case ContentBodyTypeEnum.photo:
      return { alt_text: f.title ?? "", caption: "" }
    case ContentBodyTypeEnum.article:
    default:
      return {
        body: f.title
          ? `${f.title}\n\n${result.source ?? ""}`.trim()
          : "Saved link",
      }
  }
}
